/**
 * Keeps the running page honest about the build that is live, and registers the service worker.
 *
 * - The worker serves the app shell network-first (client/vite.config.ts), so a normal refresh always boots the
 *   live build; hashed assets stay precached.
 * - While a page stays open, this module compares the build it is running (its own entry script, passed in by
 *   main.tsx) with the deployed one (/version.json, written by client/vite-version-plugin.ts) and flips a small
 *   store the in-app notice reads. It NEVER reloads the page: the person chooses when (a reload can drop
 *   unsaved input or kill an upload; see lib/busy.ts).
 *
 * No browser global is touched at module scope, so the pure parts are unit-tested in Node.
 */

const FIRST_CHECK_MS = 20_000 // one early check, in case a deploy landed while this page was loading
const POLL_MS = 5 * 60_000 // then every five minutes
const THROTTLE_MS = 30_000 // focus and visibility can fire in bursts; one shared throttle covers every trigger
// The worker itself is re-checked only hourly. An activation prunes the precache under open tabs, and the shell
// no longer needs it online, so eager worker updates buy nothing. The browser also re-checks on navigation.
const SW_UPDATE_MS = 60 * 60_000

/** True only when both are real entry paths and they differ: the page is running an older (or other) build. */
export function isNewerBuild(running: string | null | undefined, deployed: string | null | undefined): boolean {
  return typeof running === 'string' && typeof deployed === 'string'
    && running.startsWith('/assets/') && deployed.startsWith('/assets/') && running !== deployed
}

/** The notice is for the app itself: never on public share pages, marketing, the blog, or agency sites. */
const NOTICE_PATHS = ['/app', '/portal', '/booking', '/family', '/onboarding']
export function showsUpdateNotice(pathname: string, customHost: boolean): boolean {
  return !customHost && NOTICE_PATHS.some((p) => pathname === p || pathname.startsWith(p + '/'))
}

/**
 * The deployed entry from /version.json, or null on ANY failure (offline, a non-JSON SPA fallback, a malformed
 * body), so a failed check never raises the notice.
 */
export async function fetchDeployed(fetchImpl: typeof fetch = fetch, now: number = Date.now()): Promise<string | null> {
  try {
    const res = await fetchImpl(`/version.json?t=${now}`, { cache: 'no-store' })
    if (!res.ok || !(res.headers.get('content-type') ?? '').includes('application/json')) return null
    const entry = ((await res.json()) as { entry?: unknown } | null)?.entry
    return typeof entry === 'string' && entry.startsWith('/assets/') ? entry : null
  } catch {
    return null
  }
}

export interface UpdateState {
  /** The entry script this page booted. */
  running: string
  /** The entry that is live, once a check has seen it. */
  deployed: string | null
  /** The deployed entry the person said "Later" to. A different deploy shows the notice again. */
  dismissed: string | null
}

/** Whether the notice should be up for this state. */
export const updateAvailable = (s: UpdateState): boolean => isNewerBuild(s.running, s.deployed) && s.deployed !== s.dismissed

let state: UpdateState = { running: '', deployed: null, dismissed: null }
const listeners = new Set<() => void>()
const emit = () => listeners.forEach((l) => l())

export function subscribeUpdate(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}
export const getUpdateState = (): UpdateState => state

export function dismissUpdate(): void {
  if (state.deployed && state.deployed !== state.dismissed) {
    state = { ...state, dismissed: state.deployed }
    emit()
  }
}

let lastCheck = 0
let inFlight: Promise<void> | null = null

/** Look up the live build. Throttled and de-duplicated; `force` skips the throttle (a worker just took over). */
export function checkForUpdate(force = false): Promise<void> {
  if (inFlight) return inFlight
  const now = Date.now()
  if (!force && now - lastCheck < THROTTLE_MS) return Promise.resolve()
  lastCheck = now
  inFlight = fetchDeployed(fetch, now)
    .then((entry) => {
      if (entry && entry !== state.deployed) {
        state = { ...state, deployed: entry }
        emit()
      }
    })
    .finally(() => {
      inFlight = null
    })
  return inFlight
}

let started = false

/** Start watching (production only; main.tsx calls it once with the entry this page booted). */
export function startAppUpdates(running: string): void {
  if (started) return
  started = true
  state = { ...state, running }

  if ('serviceWorker' in navigator) {
    // A new worker taking over means a new client build was installed: confirm it and tell the person. Re-armed
    // on every change, so a page that got its first controller mid-life still reacts to the next deploy.
    let wasControlled = Boolean(navigator.serviceWorker.controller)
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (wasControlled) void checkForUpdate(true)
      wasControlled = true
    })
    const register = () => {
      navigator.serviceWorker
        .register('/sw.js', { scope: '/' })
        .then((reg) => {
          setInterval(() => {
            reg.update().catch(() => {}) // offline: try again next hour
          }, SW_UPDATE_MS)
        })
        .catch(() => {}) // no worker (private mode, blocked): the page still works, just uncached
    }
    if (document.readyState === 'complete') register()
    else window.addEventListener('load', register, { once: true })
  }

  setTimeout(() => void checkForUpdate(), FIRST_CHECK_MS)
  setInterval(() => void checkForUpdate(), POLL_MS)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void checkForUpdate()
  })
  window.addEventListener('focus', () => void checkForUpdate())
}

/** Load the live build. The shell is network-first, so a plain reload is enough. */
export function applyUpdate(): void {
  location.reload()
}
